import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Radio, Plus, Pencil, Trash2, Search, X, Loader2, CheckCircle2, AlertCircle, MapPin, LocateFixed, Users, Eye, Navigation } from "lucide-react";
import { PhotoPicker, AuthImage, uploadFoto } from "../components/common/PhotoField";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents, LayersControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix leaflet default marker
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_CENTER = [-6.9147, 107.6098]; // Bandung

const EMPTY = {
  kode: "",
  nama: "",
  wilayah_id: "",
  lokasi_gang: "",
  desa: "",
  kecamatan: "",
  kabupaten: "",
  kapasitas: 8,
  terpakai: 0,
  lat: DEFAULT_CENTER[0],
  lng: DEFAULT_CENTER[1],
  status: "normal",
  area: "",
  olt_uplink: "OLT-ZTE-C320-P1",
};

const RecenterMap = ({ position }) => {
  const map = useMap();
  useEffect(() => { if (position) map.setView(position, 16); }, [position, map]);
  return null;
};

const MapClickHandler = ({ onPick }) => {
  useMapEvents({ click(e) { onPick([e.latlng.lat, e.latlng.lng]); } });
  return null;
};

const OdpForm = ({ initial, wilayahList, onClose, onSuccess }) => {
  const [form, setForm] = useState(initial || EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locating, setLocating] = useState(false);
  const [fotoFile, setFotoFile] = useState(null);
  const [removeFoto, setRemoveFoto] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const position = [parseFloat(form.lat) || DEFAULT_CENTER[0], parseFloat(form.lng) || DEFAULT_CENTER[1]];

  const handleWilayahChange = (id) => {
    setField("wilayah_id", id);
    const w = wilayahList.find((x) => x.id === id);
    if (w) {
      setForm((f) => ({
        ...f,
        wilayah_id: id,
        desa: w.desa || "",
        kecamatan: w.kecamatan || "",
        kabupaten: w.kabupaten || "",
        area: w.nama_wilayah || "",
      }));
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Browser tidak support geolocation");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        setLocating(false);
      },
      (err) => {
        setError(`Gagal deteksi lokasi: ${err.message}. Izinkan GPS di browser.`);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handlePickOnMap = (latlng) => {
    setForm((f) => ({ ...f, lat: latlng[0], lng: latlng[1] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng), kapasitas: parseInt(form.kapasitas), terpakai: parseInt(form.terpakai) };
      let odpId = initial?.id;
      if (odpId) await axios.put(`${BACKEND_URL}/api/master/odp/${odpId}`, payload, { withCredentials: true });
      else odpId = (await axios.post(`${BACKEND_URL}/api/master/odp`, payload, { withCredentials: true })).data.id;
      try {
        if (fotoFile) await uploadFoto(`/api/master/odp/${odpId}/foto`, fotoFile);
        else if (removeFoto && initial?.foto_path) await axios.delete(`${BACKEND_URL}/api/master/odp/${odpId}/foto`, { withCredentials: true });
      } catch (err) {
        onSuccess();
        const msg = `Data ODP tersimpan, tapi foto gagal diunggah: ${err.response?.data?.detail || "coba lagi"}`;
        if (!initial?.id) { alert(`${msg}. Unggah ulang lewat tombol Edit.`); onClose(); return; }
        setError(msg);
        return;
      }
      onSuccess(); onClose();
    } catch (err) { setError(err.response?.data?.detail || "Gagal simpan ODP"); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-[#0e1628] z-10">
          <h2 className="text-base font-bold text-white flex items-center gap-2"><Radio className="w-4 h-4 text-cyan-400" />{initial?.id ? "Edit ODP" : "Tambah ODP"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {error && <div className="md:col-span-2 p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

          {/* Left: form fields */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Kode ODP</label>
                <input required type="text" value={form.kode} onChange={(e) => setField("kode", e.target.value.toUpperCase())} placeholder="ODP-BDG-01" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="odp-kode-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Jumlah Port</label>
                <input required type="number" min={1} value={form.kapasitas} onChange={(e) => setField("kapasitas", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="odp-kapasitas-input" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Nama ODP</label>
              <input required type="text" value={form.nama} onChange={(e) => setField("nama", e.target.value)} placeholder="ODP Dago Atas 1" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="odp-nama-input" />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Wilayah</label>
              <select required value={form.wilayah_id} onChange={(e) => handleWilayahChange(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="odp-wilayah-select">
                <option value="">-- Pilih Wilayah --</option>
                {wilayahList.map((w) => <option key={w.id} value={w.id}>{w.kode_wilayah} · {w.nama_wilayah}</option>)}
              </select>
              {form.wilayah_id && (
                <p className="text-[10px] text-slate-500 mt-1">
                  Auto: {form.desa}, {form.kecamatan}, {form.kabupaten}
                </p>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Lokasi / Gang</label>
              <input type="text" value={form.lokasi_gang} onChange={(e) => setField("lokasi_gang", e.target.value)} placeholder="Gang Mawar RT 05 RW 02" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="odp-lokasi-input" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Latitude</label>
                <input required type="number" step="any" value={form.lat} onChange={(e) => setField("lat", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="odp-lat-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Longitude</label>
                <input required type="number" step="any" value={form.lng} onChange={(e) => setField("lng", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="odp-lng-input" />
              </div>
            </div>

            <button type="button" onClick={handleUseCurrentLocation} disabled={locating} className="w-full py-2 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 text-cyan-300 text-xs font-bold flex items-center justify-center gap-2 hover:from-cyan-500/30 hover:to-blue-500/30 disabled:opacity-50" data-testid="use-current-location-btn">
              {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5" />}
              {locating ? "Mendeteksi GPS..." : "Gunakan Lokasi Saya Sekarang"}
            </button>

            <PhotoPicker
              label="Foto ODP"
              existingPath={removeFoto ? null : initial?.foto_path}
              file={fotoFile}
              onFileChange={(f) => { setFotoFile(f); if (f) setRemoveFoto(false); }}
              onRemoveExisting={() => setRemoveFoto(true)}
              testid="odp-foto"
            />
          </div>

          {/* Right: satellite map */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-300 block flex items-center gap-1"><MapPin className="w-3 h-3 text-cyan-400" />Klik di peta untuk set titik ODP</label>
            <div className="rounded-xl overflow-hidden border border-slate-700" style={{ height: "320px" }} data-testid="odp-map-container">
              <MapContainer center={position} zoom={16} style={{ height: "100%", width: "100%" }}>
                <LayersControl position="topright">
                  <LayersControl.BaseLayer checked name="Satellite">
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      attribution="Tiles &copy; Esri"
                      maxZoom={19}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="Street">
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
                      attribution="&copy; OpenStreetMap"
                    />
                  </LayersControl.BaseLayer>
                </LayersControl>
                <Marker position={position} />
                <RecenterMap position={position} />
                <MapClickHandler onPick={handlePickOnMap} />
              </MapContainer>
            </div>
            <p className="text-[10px] text-slate-500">Tips: Zoom in dan klik atap rumah/tiang untuk titik presisi</p>
          </div>

          {/* Actions */}
          <div className="md:col-span-2 flex gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700">Batal</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="odp-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {initial?.id ? "Update ODP" : "Simpan ODP"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const OdpDetail = ({ odp, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="odp-detail-modal">
    <div className="w-full max-w-lg rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl max-h-[92vh] overflow-y-auto">
      <div className="p-4 border-b border-slate-800 flex justify-between items-center">
        <div>
          <h2 className="text-sm font-bold text-white flex items-center gap-2"><Radio className="w-4 h-4 text-cyan-400" />{odp.kode} · {odp.nama}</h2>
          <p className="text-[11px] text-slate-400">{[odp.lokasi_gang, odp.wilayah ? `${odp.wilayah.nama_wilayah}, ${odp.wilayah.desa}` : ""].filter(Boolean).join(" — ") || "-"}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="odp-detail-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-3">
        <AuthImage path={odp.foto_path} alt={`Foto ${odp.kode}`} className="w-full h-64 rounded-xl" emptyText="Belum ada foto ODP" testid="odp-detail-foto" />
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="p-2 rounded-xl bg-slate-900 border border-slate-800"><p className="text-[10px] text-slate-500">Port</p><p className="font-mono font-bold text-white">{odp.kapasitas}</p></div>
          <div className="p-2 rounded-xl bg-slate-900 border border-slate-800"><p className="text-[10px] text-slate-500">Terpakai</p><p className="font-mono font-bold text-amber-400">{odp.terpakai || 0}</p></div>
          <div className="p-2 rounded-xl bg-slate-900 border border-slate-800"><p className="text-[10px] text-slate-500">Pelanggan</p><p className="font-mono font-bold text-emerald-400">{odp.pelanggan_terpasang || 0}</p></div>
        </div>
        <a href={`https://www.google.com/maps/dir/?api=1&destination=${odp.lat},${odp.lng}`} target="_blank" rel="noreferrer" className="w-full py-2 rounded-xl bg-cyan-600/20 border border-cyan-600/40 text-cyan-300 text-xs font-bold flex items-center justify-center gap-2" data-testid="odp-detail-maps">
          <Navigation className="w-3.5 h-3.5" />Buka Rute ke ODP
        </a>
      </div>
    </div>
  </div>
);

export const MasterOdpPage = () => {
  const [items, setItems] = useState([]);
  const [wilayahList, setWilayahList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [odpRes, wilRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/master/odp`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/master/wilayah`, { withCredentials: true }),
      ]);
      setItems(odpRes.data);
      setWilayahList(wilRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, nama) => {
    if (!window.confirm(`Hapus ODP "${nama}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/master/odp/${id}`, { withCredentials: true }); load(); }
    catch (err) { alert(err.response?.data?.detail || "Gagal hapus"); }
  };

  const filtered = items.filter((o) => !search ||
    o.nama?.toLowerCase().includes(search.toLowerCase()) ||
    o.kode?.toLowerCase().includes(search.toLowerCase()) ||
    o.wilayah?.nama_wilayah?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="odp-page">
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col md:flex-row justify-between md:items-center gap-3">
        <div>
          <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-[10px] font-bold uppercase">Master Data</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Data ODP (Optical Distribution Point)</h1>
          <p className="text-xs text-slate-400 mt-0.5">Kelola titik distribusi fiber. Pasang pin di peta satelit + auto-fill wilayah.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-cyan-600/30 flex items-center gap-2" data-testid="add-odp-btn">
          <Plus className="w-4 h-4" />Tambah ODP
        </button>
      </div>

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari kode/nama/wilayah..." className="w-full pl-9 pr-3 py-1.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" />
          </div>
          <span className="text-xs text-slate-500">Total ODP: <strong className="text-cyan-400">{filtered.length}</strong></span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="odp-table">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400">
                <th className="py-2.5 px-3">Kode</th>
                <th className="py-2.5 px-3">Nama ODP</th>
                <th className="py-2.5 px-3">Wilayah / Alamat</th>
                <th className="py-2.5 px-3">Gang</th>
                <th className="py-2.5 px-3 text-center">Port</th>
                <th className="py-2.5 px-3 text-center">Terpakai</th>
                <th className="py-2.5 px-3 text-center">Pelanggan</th>
                <th className="py-2.5 px-3">Koordinat</th>
                <th className="py-2.5 px-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {loading ? (
                <tr><td colSpan={9} className="py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="py-6 text-center text-slate-500 italic">Belum ada ODP</td></tr>
              ) : filtered.map((o) => {
                const util = o.kapasitas ? Math.round(((o.terpakai || 0) / o.kapasitas) * 100) : 0;
                return (
                  <tr key={o.id} className="hover:bg-slate-900/40" data-testid={`odp-row-${o.id}`}>
                    <td className="py-2.5 px-3 font-mono font-bold text-cyan-300">{o.kode}</td>
                    <td className="py-2.5 px-3 font-semibold text-white">{o.nama}</td>
                    <td className="py-2.5 px-3 text-slate-300">
                      {o.wilayah ? (
                        <>
                          <div className="text-slate-200">{o.wilayah.nama_wilayah}</div>
                          <div className="text-[10px] text-slate-500">{o.wilayah.desa}, {o.wilayah.kecamatan}, {o.wilayah.kabupaten}</div>
                        </>
                      ) : (
                        <span className="text-slate-500 italic">-</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-slate-400 text-[11px]">{o.lokasi_gang || "-"}</td>
                    <td className="py-2.5 px-3 text-center font-mono text-slate-200">{o.kapasitas}</td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`font-mono font-bold ${util > 80 ? "text-rose-400" : util > 50 ? "text-amber-400" : "text-emerald-400"}`}>{o.terpakai || 0}</span>
                    </td>
                    <td className="py-2.5 px-3 text-center"><span className="px-1.5 py-0.5 rounded bg-slate-800 text-emerald-300 font-mono text-[10px] font-bold"><Users className="w-2.5 h-2.5 inline mr-1" />{o.pelanggan_terpasang || 0}</span></td>
                    <td className="py-2.5 px-3 font-mono text-[10px] text-slate-500">
                      {Number(o.lat).toFixed(5)}, {Number(o.lng).toFixed(5)}
                      <a href={`https://www.google.com/maps?q=${o.lat},${o.lng}`} target="_blank" rel="noreferrer" className="ml-1 text-cyan-400 hover:underline"><MapPin className="w-2.5 h-2.5 inline" /></a>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setDetail(o)} title="Lihat detail & foto ODP" className={`p-1 rounded bg-slate-800 hover:bg-cyan-900/60 ${o.foto_path ? "text-cyan-400" : "text-slate-400"}`} data-testid={`view-odp-${o.id}`}><Eye className="w-3 h-3" /></button>
                        <button onClick={() => { setEditing(o); setShowForm(true); }} className="p-1 rounded bg-slate-800 hover:bg-amber-900/60 text-amber-400" data-testid={`edit-odp-${o.id}`}><Pencil className="w-3 h-3" /></button>
                        <button onClick={() => handleDelete(o.id, o.nama)} className="p-1 rounded bg-slate-800 hover:bg-rose-900/60 text-rose-400" data-testid={`delete-odp-${o.id}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {detail && <OdpDetail odp={detail} onClose={() => setDetail(null)} />}
      {showForm && <OdpForm initial={editing} wilayahList={wilayahList} onClose={() => setShowForm(false)} onSuccess={load} />}
    </div>
  );
};
